import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { 
  ArrowLeft, MapPin, Phone, MessageCircle, Star, 
  CheckCircle, Clock, Package, Truck, Send, Navigation,
  CreditCard, Calendar, Map
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { VoiceAssistant } from '../ai/VoiceAssistant';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlotLabels = {
  morning: 'Manhã (08:00 - 12:00)',
  afternoon: 'Tarde (12:00 - 18:00)',
  evening: 'Noite (18:00 - 22:00)',
  night: 'Madrugada (22:00 - 08:00)'
};

const statusSteps = [
  { key: 'pending', label: 'Pendente', icon: Clock },
  { key: 'accepted', label: 'Aceite', icon: CheckCircle },
  { key: 'picked_up', label: 'Recolhido', icon: Package },
  { key: 'delivered', label: 'Entregue', icon: CheckCircle }
];

// PayPal options (constant to avoid re-creation)
const PAYPAL_OPTIONS = {
  clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || '',
  currency: 'EUR'
};

export const OrderDetail = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const chatEndRef = useRef(null);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 5000);
    return () => clearInterval(interval);
  }, [orderId, token]);

  useEffect(() => {
    if (showChat) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [showChat, orderId, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API}/chat`, 
        { order_id: orderId, message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const updateOrderStatus = async (status) => {
    try {
      await axios.patch(`${API}/orders/${orderId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchOrder();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const submitReview = async () => {
    try {
      await axios.post(`${API}/reviews`,
        { order_id: orderId, rating: reviewRating, comment: reviewComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowReview(false);
      alert('Avaliação enviada com sucesso!');
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  const handlePaymentSuccess = async (details) => {
    try {
      await axios.post(`${API}/payments/capture`,
        { order_id: orderId, paypal_order_id: details.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPayment(false);
      fetchOrder();
      alert('Pagamento realizado com sucesso!');
    } catch (error) {
      console.error('Error capturing payment:', error);
    }
  };

  const handleVoiceAction = async (command, orderId) => {
    const action = command.action;
    try {
      if (action === 'notify_on_way' || action === 'mark_picked_up' || action === 'mark_delivered') {
        const res = await axios.post(`${API}/ai/driver-status-update`, 
          { order_id: orderId, action },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(res.data?.message || 'Comando executado');
        fetchOrder();
      } else if (action === 'call_customer') {
        const response = await axios.get(`${API}/ai/customer-phone/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const phone = response.data.recipient_phone || response.data.customer_phone;
        if (phone) {
          window.location.href = `tel:${phone}`;
        } else {
          toast.error('O cliente não tem telemóvel registado');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível executar o comando');
      console.error('Error handling voice action:', error);
    }
  };

  const navigationTarget = order?.status === 'picked_up' ? 'delivery' : 'pickup';

  const openNavigation = (app) => {
    if (!order) return;
    const addr = navigationTarget === 'delivery' ? order.delivery_address : order.pickup_address;
    const hasCoords = typeof addr?.latitude === 'number' && typeof addr?.longitude === 'number';
    const text = typeof addr === 'string'
      ? addr
      : [[addr?.street, addr?.number].filter(Boolean).join(' '), addr?.zip_code, addr?.city, 'Portugal'].filter(Boolean).join(', ');
    const query = encodeURIComponent(text);
    const coords = hasCoords ? `${addr.latitude},${addr.longitude}` : null;

    const url = app === 'waze'
      ? (coords ? `https://waze.com/ul?ll=${coords}&navigate=yes` : `https://waze.com/ul?q=${query}&navigate=yes`)
      : `https://www.google.com/maps/dir/?api=1&destination=${coords || query}&travelmode=driving`;
    window.open(url, '_blank', 'noopener');
    setShowNavigationModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">A carregar...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center px-6" data-testid="order-not-found"><div className="text-gray-700 font-semibold">Este pedido já não está disponível</div><div className="text-gray-500 text-sm mt-1">Pode ter sido aceite por outro estafeta ou cancelado.</div><Link to="/" className="inline-block mt-4 text-tuggo-mint font-medium">Voltar ao início</Link></div>
      </div>
    );
  }

  const currentStepIndex = statusSteps.findIndex(s => s.key === order.status);
  const isDriver = user?.user_type === 'driver';
  const isCustomer = user?.user_type === 'customer';

  return (
    <div className="min-h-screen bg-gray-100" data-testid="order-detail">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to={isDriver ? '/driver' : '/dashboard'} 
              className="flex items-center gap-2 text-gray-600 hover:text-tuggo-mint"
            >
              <ArrowLeft className="w-5 h-5" />
              Voltar
            </Link>
            <div className="text-sm text-gray-500">Pedido #{orderId.slice(0, 8)}</div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Progress */}
        {order.status !== 'cancelled' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              {statusSteps.map((step, index) => (
                <div key={step.key} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      index <= currentStepIndex 
                        ? 'bg-tuggo-mint text-white' 
                        : 'bg-gray-200 text-gray-400'
                    }`}>
                      <step.icon className="w-6 h-6" />
                    </div>
                    <div className={`text-sm mt-2 ${
                      index <= currentStepIndex ? 'text-tuggo-mint font-medium' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </div>
                  </div>
                  {index < statusSteps.length - 1 && (
                    <div className={`w-16 h-1 mx-2 ${
                      index < currentStepIndex ? 'bg-tuggo-mint' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Map Placeholder */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          <div className="h-48 bg-gray-200 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Mapa de rastreamento</p>
              <p className="text-sm text-gray-400">Localização em tempo real</p>
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Detalhes da Entrega</h3>
          
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-500 mb-1">Item</div>
              <div className="font-medium text-gray-900">{order.description}</div>
              <div className="text-sm text-tuggo-mint">{order.category}</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full mt-1" />
                  <div>
                    <div className="text-sm text-gray-500">Recolha</div>
                    <div className="text-gray-900">
                      {order.pickup_address.street}, {order.pickup_address.number}
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.pickup_address.neighborhood}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-1" />
                  <div>
                    <div className="text-sm text-gray-500">Entrega</div>
                    <div className="text-gray-900">
                      {order.delivery_address.street}, {order.delivery_address.number}
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.delivery_address.neighborhood}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500 mb-1">Destinatário</div>
              <div className="font-medium text-gray-900">{order.recipient_name}</div>
              <div className="text-sm text-gray-500">{order.recipient_phone}</div>
            </div>

            {/* Scheduled Delivery Info */}
            {order.is_scheduled && order.scheduled_date && (
              <div className="bg-tuggo-mint/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-tuggo-mint" />
                  <span className="font-semibold text-gray-900">Entrega Agendada</span>
                </div>
                <div className="text-gray-700">
                  <span className="font-medium">
                    {new Date(order.scheduled_date).toLocaleDateString('pt-BR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long'
                    })}
                  </span>
                  {order.scheduled_time_slot && (
                    <span className="text-gray-500 ml-2">
                      • {timeSlotLabels[order.scheduled_time_slot] || order.scheduled_time_slot}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Valor</div>
                  <div className="text-2xl font-bold text-tuggo-mint">
                    € {(order.final_price || order.estimated_price).toFixed(2)}
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  order.payment_status === 'paid' 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {order.payment_status === 'paid' ? 'Pago' : 'Pagamento pendente'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Driver Info (for customers) */}
        {isCustomer && order.driver_id && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Estafeta</h3>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-tuggo-mint rounded-full flex items-center justify-center">
                <Truck className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{order.driver_name}</div>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  4.9
                </div>
              </div>
              <Button
                onClick={() => setShowChat(true)}
                variant="outline"
                className="rounded-full"
                data-testid="open-chat-btn"
              >
                <MessageCircle className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Customer Info (for drivers) */}
        {isDriver && order.customer_id && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Cliente</h3>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center">
                <Package className="w-7 h-7 text-gray-500" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{order.customer_name}</div>
              </div>
              <Button
                onClick={() => setShowChat(true)}
                variant="outline"
                className="rounded-full"
              >
                <MessageCircle className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Voice Assistant for Drivers */}
        {isDriver && ['accepted', 'picked_up'].includes(order.status) && (
          <VoiceAssistant 
            orderId={orderId}
            onAction={handleVoiceAction}
            userType="driver"
          />
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Navigation Button for Drivers */}
          {isDriver && ['accepted', 'picked_up'].includes(order.status) && (
            <Button
              onClick={() => setShowNavigationModal(true)}
              className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-full py-6"
              data-testid="follow-route-btn"
            >
              <Navigation className="w-5 h-5 mr-2" />
              {navigationTarget === 'delivery' ? 'Navegar para Entrega' : 'Navegar para Recolha'}
            </Button>
          )}

          {/* Driver Actions */}
          {isDriver && order.status === 'accepted' && (
            <Button
              onClick={() => updateOrderStatus('picked_up')}
              className="w-full bg-tuggo-mint text-white rounded-full py-6"
              data-testid="pickup-btn"
            >
              <Package className="w-5 h-5 mr-2" />
              Confirmar Recolha
            </Button>
          )}

          {isDriver && order.status === 'picked_up' && (
            <Button
              onClick={() => updateOrderStatus('delivered')}
              className="w-full bg-green-600 text-white hover:bg-green-700 rounded-full py-6"
              data-testid="deliver-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Confirmar Entrega
            </Button>
          )}

          {/* Customer Actions */}
          {isCustomer && order.status === 'delivered' && order.payment_status !== 'paid' && (
            <Button
              onClick={() => setShowPayment(true)}
              className="w-full bg-tuggo-mint text-white rounded-full py-6"
              data-testid="pay-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Pagar com PayPal
            </Button>
          )}

          {isCustomer && order.status === 'delivered' && (
            <Button
              onClick={() => setShowReview(true)}
              variant="outline"
              className="w-full rounded-full py-6"
              data-testid="review-btn"
            >
              <Star className="w-5 h-5 mr-2" />
              Avaliar Estafeta
            </Button>
          )}

          {order.status === 'pending' && (
            <Button
              onClick={() => updateOrderStatus('cancelled')}
              variant="ghost"
              className="w-full text-red-600 hover:bg-red-50 rounded-full py-6"
              data-testid="cancel-btn"
            >
              Cancelar Pedido
            </Button>
          )}
        </div>
      </div>

      {/* Chat Modal */}
      {showChat && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Chat</h3>
              <button onClick={() => setShowChat(false)} className="text-gray-500">
                ×
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.sender_id === user?.id
                      ? 'bg-tuggo-mint text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    <div className="text-sm">{msg.message}</div>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <form onSubmit={sendMessage} className="p-4 border-t flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Escreva a sua mensagem..."
                className="flex-1"
                data-testid="chat-input"
              />
              <Button type="submit" className="bg-tuggo-mint text-white rounded-full px-4">
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4">Pagamento via PayPal</h3>
            <div className="mb-6">
              <div className="text-3xl font-bold text-tuggo-mint mb-2">
                € {(order.final_price || order.estimated_price).toFixed(2)}
              </div>
              <p className="text-gray-500">Pagamento pela entrega</p>
            </div>
            
            {process.env.REACT_APP_PAYPAL_CLIENT_ID ? (
              <PayPalScriptProvider options={PAYPAL_OPTIONS}>
                <PayPalButtons
                  createOrder={(data, actions) => {
                    return actions.order.create({
                      purchase_units: [{
                        amount: {
                          value: (order.final_price || order.estimated_price).toFixed(2),
                          currency_code: 'EUR'
                        },
                        description: `Entrega TugGo - ${order.description}`
                      }]
                    });
                  }}
                  onApprove={(data, actions) => {
                    return actions.order.capture().then(handlePaymentSuccess);
                  }}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    alert('Erro no pagamento. Tente novamente.');
                  }}
                />
              </PayPalScriptProvider>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500 mb-4">PayPal não configurado</p>
                <Button
                  onClick={() => handlePaymentSuccess({ id: 'mock-payment-' + Date.now() })}
                  className="w-full bg-tuggo-mint text-white rounded-full"
                  data-testid="mock-pay-btn"
                >
                  Simular Pagamento
                </Button>
              </div>
            )}
            
            <Button
              onClick={() => setShowPayment(false)}
              variant="ghost"
              className="w-full mt-4"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Navigation Modal */}
      {showNavigationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Escolher App de Navegação</h3>
            <p className="text-gray-600 mb-6">
              Destino ({navigationTarget === 'delivery' ? 'entrega' : 'recolha'}):{' '}
              <span className="font-medium text-gray-900" data-testid="navigation-destination">
                {(() => {
                  const a = navigationTarget === 'delivery' ? order.delivery_address : order.pickup_address;
                  return typeof a === 'string' ? a : [[a?.street, a?.number].filter(Boolean).join(' '), a?.zip_code, a?.city].filter(Boolean).join(', ');
                })()}
              </span>
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={() => openNavigation('maps')}
                className="w-full bg-white border-2 border-gray-200 hover:border-tuggo-mint text-gray-900 rounded-xl py-4 flex items-center justify-center gap-3"
                data-testid="navigate-google-maps-btn"
              >
                <Map className="w-6 h-6 text-blue-600" />
                <span className="font-semibold">Google Maps</span>
              </Button>
              
              <Button
                onClick={() => openNavigation('waze')}
                className="w-full bg-white border-2 border-gray-200 hover:border-tuggo-mint text-gray-900 rounded-xl py-4 flex items-center justify-center gap-3"
                data-testid="navigate-waze-btn"
              >
                <Navigation className="w-6 h-6 text-blue-500" />
                <span className="font-semibold">Waze</span>
              </Button>
            </div>
            
            <Button
              onClick={() => setShowNavigationModal(false)}
              variant="outline"
              className="w-full mt-4 rounded-xl"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4">Avaliar Estafeta</h3>
            
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setReviewRating(star)}
                  className="focus:outline-none"
                  data-testid={`star-${star}`}
                >
                  <Star className={`w-10 h-10 ${
                    star <= reviewRating
                      ? 'text-yellow-500 fill-yellow-500'
                      : 'text-gray-300'
                  }`} />
                </button>
              ))}
            </div>

            <textarea
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="Deixe um comentário (opcional)"
              className="w-full p-4 border rounded-xl resize-none h-24 mb-4"
              data-testid="review-comment"
            />

            <div className="flex gap-3">
              <Button
                onClick={() => setShowReview(false)}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Cancelar
              </Button>
              <Button
                onClick={submitReview}
                className="flex-1 bg-tuggo-mint text-white rounded-full"
                data-testid="submit-review-btn"
              >
                Enviar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
